import { useState } from 'react';
import { Check, MapPin, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface LocationOption {
  id: string;
  name: string;
}

export interface LocationSelection {
  locationId: string;
  priceOverride?: number | null;
  costOverride?: number | null;
  skuOverride?: string | null;
  taxRateOverride?: number | null;
  isAvailable?: boolean;
}

interface LocationSelectorProps {
  locations: LocationOption[];
  selectedLocations: string[];
  onSelectionChange: (locationIds: string[]) => void;
  disabled?: boolean;
  className?: string;
}

export function LocationSelector({
  locations,
  selectedLocations,
  onSelectionChange,
  disabled = false,
  className,
}: LocationSelectorProps) {
  const [open, setOpen] = useState(false);

  const allSelected = selectedLocations.length === locations.length && locations.length > 0;

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(locations.map(l => l.id));
    }
  };

  const handleToggleLocation = (locationId: string) => {
    if (selectedLocations.includes(locationId)) {
      onSelectionChange(selectedLocations.filter(id => id !== locationId));
    } else {
      onSelectionChange([...selectedLocations, locationId]);
    }
  };

  const getDisplayText = () => {
    if (selectedLocations.length === 0) {
      return 'Select locations';
    }
    if (allSelected) {
      return 'All locations';
    }
    if (selectedLocations.length === 1) {
      const loc = locations.find(l => l.id === selectedLocations[0]);
      return loc?.name || '1 location';
    }
    return `${selectedLocations.length} locations`;
  };

  return (
    <div className={cn('space-y-2', className)}>
      <Label>Locations</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            disabled={disabled}
          >
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className={selectedLocations.length === 0 ? 'text-muted-foreground' : ''}>
                {getDisplayText()}
              </span>
            </div>
            {selectedLocations.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {selectedLocations.length}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <div className="p-2 border-b">
            <div
              className={cn(
                'flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted/50',
                allSelected && 'bg-primary/10'
              )}
              onClick={handleSelectAll}
            >
              <Checkbox
                checked={allSelected}
                className="data-[state=checked]:bg-primary"
              />
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">All Locations</span>
              {allSelected && (
                <Check className="h-4 w-4 ml-auto text-primary" />
              )}
            </div>
          </div>
          <div className="max-h-[200px] overflow-y-auto p-2">
            {locations.map((location) => {
              const isSelected = selectedLocations.includes(location.id);
              return (
                <div
                  key={location.id}
                  className={cn(
                    'flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted/50',
                    isSelected && 'bg-primary/5'
                  )}
                  onClick={() => handleToggleLocation(location.id)}
                >
                  <Checkbox
                    checked={isSelected}
                    className="data-[state=checked]:bg-primary"
                  />
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{location.name}</span>
                  {isSelected && (
                    <Check className="h-4 w-4 ml-auto text-primary" />
                  )}
                </div>
              );
            })}
          </div>
          {locations.length === 0 && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No locations available
            </div>
          )}
        </PopoverContent>
      </Popover>
      {selectedLocations.length > 0 && selectedLocations.length < locations.length && (
        <div className="flex flex-wrap gap-1">
          {selectedLocations.slice(0, 3).map(locId => {
            const loc = locations.find(l => l.id === locId);
            return (
              <Badge key={locId} variant="secondary" className="text-xs">
                {loc?.name}
              </Badge>
            );
          })}
          {selectedLocations.length > 3 && (
            <Badge variant="secondary" className="text-xs">
              +{selectedLocations.length - 3} more
            </Badge>
          )}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Select which locations this product should be available at
      </p>
    </div>
  );
}

